import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card } from '@diwata/ironvale'
import {
  MissionsAPI,
  type Mission,
  type MissionProposal,
  type MissionTemplate,
  type ProposedMission,
} from '@/api/missions'
import { allShelfTags, filterByTags, groupByShelf } from '@/lib/sphereShelves'
import { useMissions } from '@/store/missions'
import { useUI } from '@/store/ui'
import { HelpHint } from '@/components/HelpHint'
import { FlowDiagram } from '@/components/FlowDiagram'
import { ProposalCard } from '@/components/ProposalCard'
import './missions-agents.css'

// The Missions ATLAS: every mission as a card, mirroring the /domains list. Gated missions —
// the ones awaiting YOUR judgment — surface first, then what's in flight, then history. Each
// card is a door to /missions/:id, where the machine's work becomes the whole page.

type Group = 'gate' | 'active' | 'done'

/** Where a mission sits, read from its stored steps + outcome (the list has no live run). */
function groupOf(m: Mission): Group {
  const done = Boolean(m.outcome) || (m.steps.length > 0 && m.steps.every((s) => s.status === 'done'))
  if (done) return 'done'
  if (m.steps.some((s) => s.status === 'awaiting_gate')) return 'gate'
  return 'active'
}

/** The plain-words status a keeper reads on the card — never the raw StepStatus. */
const PLAIN: Record<Group, { label: string; cls: string }> = {
  gate: { label: 'waiting for your call', cls: 'gate' },
  active: { label: 'Tala is working', cls: 'active' },
  done: { label: 'done', cls: 'done' },
}

function MissionCard({ mission }: { mission: Mission }) {
  const group = groupOf(mission)
  const plain = PLAIN[group]
  const to = `/missions/${mission.id}`
  return (
    <Card className="dw-card-stack">
      <div className="dw-card-stack-body">
        <div style={{ fontWeight: 600, letterSpacing: '0.03em', marginBottom: 6 }}>
          <Link to={to} className="dw-domain-card-link" title={`open ${mission.title}`}>
            {mission.title}
          </Link>
        </div>
        <div className="dw-mission-card-meta">
          <span className="dw-chip">{mission.domain}</span>
          <span className={`dw-mission-status dw-mission-status--${plain.cls}`}>
            <span className="dw-mission-status-dot" aria-hidden="true">●</span>
            {plain.label}
          </span>
        </div>
        <div className="dw-mission-card-goal">{mission.goal || '—'}</div>
        <div className="dw-card-footer">
          <span className="dw-inbox-detail">target / {mission.target}</span>
          <Link to={to} className="dw-inline-link" style={{ whiteSpace: 'nowrap' }} title={`open ${mission.title}`}>
            open →
          </Link>
        </div>
      </div>
    </Card>
  )
}

/** The empty state teaches what a mission is — preserved verbatim from the single-page era. */
function MissionsTeach() {
  return (
    <div className="dw-teach">
      <div className="dw-section-title">What a mission is</div>
      <p className="dw-teach-lede">
        A mission is work you hand to ✦ Tala: she runs the steps, you judge the result.
      </p>
      <p className="dw-teach-contrast">
        Unlike a note, a mission <em>does something</em> — it drafts, researches, or opens a path —
        and then stops at a <HelpHint term="gate">gate</HelpHint> for your call. Nothing ships
        without your judgment.
      </p>

      <div className="dw-teach-steps" aria-hidden="true">
        <span className="dw-teach-step"><span className="dw-teach-step-glyph">◉</span> steps</span>
        <span className="dw-teach-arrow">→</span>
        <span className="dw-teach-step"><span className="dw-teach-step-glyph">◇</span> gate</span>
        <span className="dw-teach-arrow">→</span>
        <span className="dw-teach-step"><span className="dw-teach-step-glyph">●</span> outcome</span>
      </div>

      <div className="dw-section-title" style={{ marginTop: 22 }}>Start one</div>
      <div className="dw-teach-actions">
        <Link className="dw-teach-action" to="/signals">
          <span className="dw-teach-action-title">✦ Open a signal as a mission →</span>
          <span className="dw-teach-action-sub">
            A signal is an arrival that crossed your rules — turn one into work.
          </span>
        </Link>
        <Link className="dw-teach-action" to="/domains">
          <span className="dw-teach-action-title">Start from a domain →</span>
          <span className="dw-teach-action-sub">
            Pick a target in one of your domains and give Tala a goal.
          </span>
        </Link>
      </div>
      <p className="dw-teach-foot">
        Or state a goal above — Tala drafts the mission for you to approve.
      </p>
    </div>
  )
}

/** Does this recipe's goal carry a {target} the operator must name before instantiating? */
function needsTarget(t: MissionTemplate): boolean {
  return t.goal_template.includes('{target}')
}

/** Fill the goal preview with the typed target and the recipe's domain (or soft placeholders),
 *  so the card shows the actual goal the mission will carry — not a raw template string.
 *  Both {target} and {domain} are substituted; the backend fills the same tokens on instantiate. */
function fillGoal(goalTemplate: string, target: string, domain: string): string {
  return goalTemplate
    .replace(/\{target\}/g, target.trim() || '…')
    .replace(/\{domain\}/g, domain.trim() || '…')
}

/** One recipe in the shelf. Collapsed to name + gloss + tags (a wall of skeletons drowns the
 *  shelf — same ruling as domain templates); "preview the recipe" discloses the goal and the
 *  step skeleton. "Use recipe" stays in the footer in both states — a recipe with a {target}
 *  reveals an inline target field there first, so acting never requires expanding. */
function RecipeCard({
  t,
  busy,
  onUse,
}: {
  t: MissionTemplate
  busy: boolean
  onUse: (t: MissionTemplate, target: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [target, setTarget] = useState('')
  const wantsTarget = needsTarget(t)
  const detailsId = `dw-recipe-inside-${t.key}`
  const canUse = !busy && (!wantsTarget || target.trim().length > 0)

  return (
    <Card className="dw-card-stack">
      <div style={{ fontWeight: 600, marginBottom: 6, letterSpacing: '0.03em' }}>{t.name}</div>
      <div style={{ fontSize: 'var(--ae-font-size-xs)', color: 'var(--ae-color-text-secondary)', marginBottom: 10 }}>
        {t.gloss}
      </div>

      {(t.tags ?? []).length > 0 && (
        <div className="dw-chip-row" style={{ marginBottom: 10 }}>
          {(t.tags ?? []).map((tag) => (
            <span key={tag} className="dw-chip">{tag}</span>
          ))}
        </div>
      )}

      <button
        type="button"
        className="dw-template-toggle"
        aria-expanded={expanded}
        aria-controls={detailsId}
        onClick={() => setExpanded((v) => !v)}
      >
        <span aria-hidden="true">{expanded ? '▾' : '▸'}</span> preview the recipe
      </button>

      {expanded && (
        <div id={detailsId}>
          <div className="dw-field-label" style={{ marginBottom: 4 }}>The goal</div>
          <div className="dw-recipe-goal">{fillGoal(t.goal_template, target, t.domain)}</div>

          <div className="dw-field-label" style={{ marginBottom: 4 }}>The steps</div>
          <ol className="dw-recipe-steps">
            {t.steps.map((s) => (
              <li key={s.id} className="dw-recipe-step">
                <span>{s.label}</span>
                {s.gate === 'review' && (
                  <span className="dw-recipe-step-gate" title="pauses here for your review">
                    ◇ gate
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="dw-recipe-foot">
        {wantsTarget && (
          <label className="dw-recipe-target">
            <span className="dw-field-label">Target</span>
            <input
              className="dw-input"
              value={target}
              placeholder="who / what this mission is about"
              onChange={(e) => setTarget(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canUse) onUse(t, target)
              }}
            />
          </label>
        )}
        <button className="dw-btn" disabled={!canUse} onClick={() => onUse(t, target)}>
          Use recipe →
        </button>
      </div>
    </Card>
  )
}

/** "Start from a recipe" — the primary +New-mission path. Recipes come from the owner-agnostic
 *  registry (GET /missions/templates), grouped into sphere shelves and tag-filtered exactly like
 *  the domain-template gallery, so a recipe reads as a sibling of a domain template. Using one
 *  POSTs /missions/from-template (a DRAFT mission with the step skeleton) and lands its page. */
function RecipePicker({
  api,
  onInstantiated,
}: {
  api: MissionsAPI
  onInstantiated: (mission: Mission) => void
}) {
  const { data: templates } = useQuery({
    queryKey: ['mission-templates'],
    queryFn: () => api.listTemplates(),
  })
  const [filter, setFilter] = useState<string[]>([])
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const all = templates ?? []
  const shelfTags = allShelfTags(all)
  const shown = filterByTags(all, filter)
  const shelves = groupByShelf(shown)

  async function use(t: MissionTemplate, target: string) {
    setBusyKey(t.key)
    setError(null)
    try {
      const mission = await api.fromTemplate({
        key: t.key,
        domain: t.domain,
        ...(needsTarget(t) && target.trim() ? { target: target.trim() } : {}),
      })
      onInstantiated(mission)
    } catch {
      setError('Could not start a mission from that recipe. Try again.')
    } finally {
      setBusyKey(null)
    }
  }

  if (all.length === 0) {
    return (
      <div className="dw-rail-empty">
        {templates === undefined ? 'Loading recipes…' : 'No recipes yet — state a goal below and Tala drafts one.'}
      </div>
    )
  }

  return (
    <div>
      {shelfTags.length > 0 && (
        <div className="dw-chip-row" style={{ marginBottom: 12 }}>
          {shelfTags.map((tag) => (
            <button
              key={tag}
              type="button"
              className={`dw-chip${filter.includes(tag) ? ' dw-chip--removable' : ''}`}
              style={{ cursor: 'pointer' }}
              aria-pressed={filter.includes(tag)}
              title={filter.includes(tag) ? `clear ${tag} filter` : `filter recipes by ${tag}`}
              onClick={() =>
                setFilter((cur) => (cur.includes(tag) ? cur.filter((x) => x !== tag) : [...cur, tag]))
              }
            >
              {tag}
              {filter.includes(tag) && <span aria-hidden="true"> ×</span>}
            </button>
          ))}
        </div>
      )}

      {shelves.map((shelf) => (
        <div key={shelf.tag ?? '(untagged)'} className="dw-shelf">
          {shelf.label && <div className="dw-field-label dw-shelf-label">{shelf.label}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {shelf.items.map((t) => (
              <RecipeCard key={t.key} t={t} busy={busyKey === t.key} onUse={use} />
            ))}
          </div>
        </div>
      ))}
      {all.length > 0 && shown.length === 0 && (
        <div className="dw-rail-empty">no recipes carry those tags — clear a chip above</div>
      )}
      {error && <div className="dw-notice dw-run-error">{error}</div>}
    </div>
  )
}

/** One of Tala's live suggestions, as a card distinct from a mission — a dashed accent edge
 *  marks it as a thing she's OFFERING, not a thing you hold yet. Three acts: Promote (run the
 *  recipe on its target and land the new mission), Edit (adjust the target inline before
 *  promoting — the recipe target-input pattern), Dismiss (suppress it; the card fades out). */
function ProposedMissionCard({ api, proposal }: { api: MissionsAPI; proposal: ProposedMission }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [editing, setEditing] = useState(false)
  const [target, setTarget] = useState(proposal.target)
  const [busy, setBusy] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canPromote = !busy && target.trim().length > 0
  const title = `${proposal.recipe_name}: ${target.trim() || proposal.target}`

  async function promote() {
    if (!canPromote) return
    setBusy(true)
    setError(null)
    try {
      // Promote uses the (possibly-edited) target; the backend instantiates the recipe and
      // writes a suppression row so this proposal never re-appears.
      const mission = await api.promoteProposal(proposal.id, target)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['missions'] }),
        qc.invalidateQueries({ queryKey: ['mission-proposals'] }),
      ])
      navigate(`/missions/${mission.id}`)
    } catch {
      setError('Could not start that mission. Try again.')
      setBusy(false)
    }
  }

  async function dismiss() {
    if (busy) return
    setBusy(true)
    setLeaving(true) // fade the card out before the refetch drops it
    try {
      await api.dismissProposal(proposal.id)
    } finally {
      await qc.invalidateQueries({ queryKey: ['mission-proposals'] })
    }
  }

  return (
    <Card className={`dw-card-stack dw-proposed-card${leaving ? ' dw-proposed-card--leaving' : ''}`}>
      <div className="dw-card-stack-body">
        <div className="dw-proposed-title" title={title}>
          {title}
        </div>
        <div className="dw-proposed-reason">{proposal.reason}</div>
        <div className="dw-mission-card-meta">
          <span className="dw-chip">{proposal.domain}</span>
          <span className="dw-proposed-flag">✦ suggested · {proposal.source_kind}</span>
        </div>

        {editing && (
          <label className="dw-recipe-target dw-proposed-edit">
            <span className="dw-field-label">Target</span>
            <input
              className="dw-input"
              value={target}
              autoFocus
              placeholder="who / what this mission is about"
              onChange={(e) => setTarget(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canPromote) promote()
              }}
            />
          </label>
        )}

        {error && <div className="dw-field-error">{error}</div>}

        <div className="dw-proposed-actions">
          <Button size="sm" variant="primary" disabled={!canPromote} onClick={promote}>
            {busy && !leaving ? 'Promoting…' : 'Promote →'}
          </Button>
          <Button size="sm" variant="ghost" aria-expanded={editing} onClick={() => setEditing((v) => !v)}>
            {editing ? 'Done' : 'Edit'}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={dismiss}>
            Dismiss
          </Button>
        </div>
      </div>
    </Card>
  )
}

/** "Tala suggests" — the live PROPOSED band atop the atlas, above "Needs your call". Reads the
 *  same ['mission-proposals'] query the ◈ rail mirrors. Empty → renders nothing (no empty box). */
function ProposedSection({ api }: { api: MissionsAPI }) {
  const { data } = useQuery({
    queryKey: ['mission-proposals'],
    queryFn: () => api.listProposals(),
  })
  const proposals = data ?? []
  if (proposals.length === 0) return null

  return (
    <div className="dw-mission-group">
      <div className="dw-rail-section-title dw-mission-group-title">
        Tala suggests{' '}
        <HelpHint gloss="Missions Tala thinks are worth starting — drawn from your fresh signals and newly-arrived stars. Promote one to run it, or dismiss it." />
        <span className="dw-mission-group-count">{proposals.length}</span>
      </div>
      <div className="dw-mission-cards">
        {proposals.map((p) => (
          <ProposedMissionCard key={p.id} api={api} proposal={p} />
        ))}
      </div>
    </div>
  )
}

export function Missions() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new MissionsAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { missions } = useMissions()

  // Goal-first creation, right here on the atlas: state a goal → Tala drafts a proposal →
  // you approve → the new mission is created and you land on its page.
  const [showCreate, setShowCreate] = useState(false)
  const [goal, setGoal] = useState('')
  const [proposal, setProposal] = useState<MissionProposal | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function propose() {
    if (!goal.trim() || busy) return
    setBusy(true)
    setError(null)
    try {
      const r = await api.propose(goal)
      setProposal(r.proposal)
    } catch {
      setError('The familiar could not draft a mission from that. Try rephrasing the goal.')
    } finally {
      setBusy(false)
    }
  }

  // A recipe was instantiated server-side (POST /missions/from-template) — refresh the atlas
  // and land the operator on the new DRAFT mission's page, where it's fully editable.
  async function onInstantiated(mission: Mission) {
    setShowCreate(false)
    await qc.invalidateQueries({ queryKey: ['missions'] })
    navigate(`/missions/${mission.id}`)
  }

  async function create(p: MissionProposal) {
    setBusy(true)
    setError(null)
    try {
      const created = await api.create({
        title: p.title.trim() || p.goal,
        goal: p.goal,
        status: 'Active',
        priority: 'High',
        domain: p.domain,
        target: p.target,
        protocol: p.protocol,
        steps: p.steps,
      })
      setProposal(null)
      setGoal('')
      setShowCreate(false)
      await qc.invalidateQueries({ queryKey: ['missions'] })
      navigate(`/missions/${created.id}`)
    } catch {
      setError('Creating the mission failed. Try again.')
    } finally {
      setBusy(false)
    }
  }

  const gated = missions.filter((m) => groupOf(m) === 'gate')
  const active = missions.filter((m) => groupOf(m) === 'active')
  const done = missions.filter((m) => groupOf(m) === 'done')

  const groups: { key: Group; title: string; items: Mission[] }[] = [
    { key: 'gate', title: 'Needs your call', items: gated },
    { key: 'active', title: 'In flight', items: active },
    { key: 'done', title: 'Done', items: done },
  ]

  return (
    <div>
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">
            Missions <HelpHint term="mission" /> <FlowDiagram />
          </div>
          <div className="dw-page-subtitle">
            Work you hand to Tala — she runs the steps, you judge the result. Each mission gets its own page.
          </div>
        </div>
        <Button
          size="sm"
          variant="primary"
          onClick={() => {
            setShowCreate((v) => !v)
            setProposal(null)
            setError(null)
          }}
        >
          {showCreate ? 'Close' : '+ New mission'}
        </Button>
      </div>

      {showCreate && (
        <Card style={{ marginBottom: 18 }}>
          {proposal ? (
            <>
              <div className="dw-rail-section-title">New mission</div>
              <ProposalCard
                proposal={proposal}
                busy={busy}
                onCreate={create}
                onDiscard={() => setProposal(null)}
              />
            </>
          ) : (
            <>
              {/* PRIMARY: start from a recipe — a shelf of pre-built missions, sibling to the
                  domain-template gallery. Using one lands an editable DRAFT on its own page. */}
              <div className="dw-rail-section-title">Start from a recipe</div>
              <div className="dw-recipe-lede">
                A recipe is a ready-made mission — a goal and its steps, already shaped. Pick one,
                name its target, and Tala runs it. You can edit everything on the mission's page.
              </div>
              <RecipePicker api={api} onInstantiated={onInstantiated} />

              {/* SECONDARY: or state a goal — the existing propose → judge → create path. */}
              <div className="dw-recipe-or">
                <span>or state a goal — Tala drafts it</span>
              </div>
              <div className="dw-goal-form">
                <textarea
                  className="dw-input dw-textarea"
                  rows={3}
                  placeholder="State the goal — Tala drafts the mission, you judge it."
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                />
                <Button size="sm" variant="primary" disabled={busy || !goal.trim()} onClick={propose}>
                  {busy ? '✦ Drafting…' : '✦ Draft mission'}
                </Button>
              </div>
            </>
          )}
          {error && <div className="dw-notice dw-run-error">{error}</div>}
        </Card>
      )}

      {/* PROPOSED — Tala's live suggestions ride the top of the atlas, above your gated work. */}
      <ProposedSection api={api} />

      {missions.length === 0 ? (
        <MissionsTeach />
      ) : (
        groups.map((g) =>
          g.items.length === 0 ? null : (
            <div key={g.key} className="dw-mission-group">
              <div className="dw-rail-section-title dw-mission-group-title">
                {g.title}
                <span className="dw-mission-group-count">{g.items.length}</span>
              </div>
              <div className="dw-mission-cards">
                {g.items.map((m) => (
                  <MissionCard key={m.id} mission={m} />
                ))}
              </div>
            </div>
          ),
        )
      )}
    </div>
  )
}
